import React, { useState, useEffect, useCallback } from "react";
import './scorepad.css';
import {
    PLAYER_KEYS,
    ROW_COLORS,
    createFreshData,
    createFreshTotal,
    DEFAULT_NAMES,
    loadFromStorage,
    saveToStorage
} from './data.js';

export default function Scorepad() {
    const saved = loadFromStorage();

    const [data, setData] = useState(saved?.data || createFreshData());
    const [playerNames, setPlayerNames] = useState(saved?.playerNames || [...DEFAULT_NAMES]);
    const [playerCount, setPlayerCount] = useState(saved?.playerCount || 4);
    const [total, setTotal] = useState(createFreshTotal());
    const [focused, setFocused] = useState(null);

    const activeKeys = PLAYER_KEYS.slice(0, playerCount);

    useEffect(() => {
        saveToStorage(data, playerNames, playerCount);
    }, [data, playerNames, playerCount]);

    useEffect(() => {
        const newTotal = createFreshTotal();
        for (let i = 0; i < data.length; i++) {
            for (const key of PLAYER_KEYS) {
                newTotal[key] += data[i][key];
            }
        }
        setTotal(newTotal);
    }, [data]);

    const handleOnChange = useCallback((index, player, value) => {
        let num = parseInt(value, 10);
        if (isNaN(num) || num < 0) num = 0;
        if (num > 999) num = 999;
        setData(prevData => {
            const newData = prevData.map((row, i) => {
                if (i !== index) return row;
                return { ...row, [player]: num };
            });
            return newData;
        });
    }, []);

    const handleOnFocus = useCallback((target) => {
        if (focused === target) return;
        setFocused(target);
        setTimeout(function () { target.select(); }, 10);
    }, [focused]);

    const handleReset = useCallback(() => {
        const fresh = createFreshData();
        setData(fresh);
        setPlayerNames([...DEFAULT_NAMES]);
    }, []);

    const handlePlayerNameChange = useCallback((playerKey, newName) => {
        setPlayerNames(prev => {
            const idx = PLAYER_KEYS.indexOf(playerKey);
            const updated = [...prev];
            updated[idx] = newName;
            return updated;
        });
    }, []);

    const handlePlayerCountChange = useCallback((count) => {
        setPlayerCount(count);
    }, []);

    const maxTotal = Math.max(...activeKeys.map(key => total[key]));
    const isWinner = (playerKey) => playerCount > 1 && total[playerKey] === maxTotal && maxTotal > 0;

    return (
        <div className="table-container">
            <header className="scorepad-header">
                <h1 className="app-title">Five Tribes</h1>
                <div className="controls">
                    <div className="player-count-control">
                        <label htmlFor="player-count">Players:</label>
                        <select
                            id="player-count"
                            value={playerCount}
                            onChange={e => handlePlayerCountChange(Number(e.target.value))}
                        >
                            <option value={2}>2</option>
                            <option value={3}>3</option>
                            <option value={4}>4</option>
                        </select>
                    </div>
                    <button className="reset-btn" onClick={handleReset}>
                        New Game
                    </button>
                </div>
            </header>
            <table className="scorepad-table">
                <thead>
                    <tr>
                        <th className="category-header">Five Tribes</th>
                        {activeKeys.map((key, i) => (
                            <th key={key} className="player-header">
                                <input
                                    className="player-name-input"
                                    type="text"
                                    maxLength={12}
                                    value={playerNames[i]}
                                    onChange={e => handlePlayerNameChange(key, e.target.value)}
                                />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((elem, index) => (
                        <tr key={"tr-" + index}>
                            <td className={`category-cell ${ROW_COLORS[index]}`}>{elem.name}</td>
                            {activeKeys.map((key) => (
                                <td key={key} className={`input small-col ${ROW_COLORS[index]}`}>
                                    <input
                                        className={ROW_COLORS[index]}
                                        type="number"
                                        min="0"
                                        max="999"
                                        step="1"
                                        value={elem[key]}
                                        onChange={e => handleOnChange(index, key, e.target.value)}
                                        onFocus={e => handleOnFocus(e.target)}
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                    <tr className="total-row">
                        <td className="total-label">Total</td>
                        {activeKeys.map((key) => (
                            <td
                                key={key}
                                className={`total small-col ${isWinner(key) ? "winner" : ""}`}
                            >
                                {total[key]}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
